<template>
  <gas-page-shell :breadcrumb="['报表管理', '年报表']">
    <div class="stat-row">
      <div v-for="item in statCards" :key="item.label" class="stat-card">
        <div class="stat-icon" :style="{ background: item.bg, color: item.color }"><i :class="item.icon"></i></div>
        <div class="stat-body">
          <div class="stat-label">{{ item.label }}</div>
          <div class="stat-value">{{ item.value }}<span v-if="item.unit" class="unit">{{ item.unit }}</span></div>
        </div>
      </div>
    </div>

    <div class="search-panel">
      <el-form :inline="true" :model="query" size="small" label-width="72px" class="search-form">
        <el-form-item label="报表年份">
          <el-date-picker v-model="query.reportYear" type="year" value-format="yyyy" placeholder="选择年份" style="width:140px" />
          <el-button class="date-shortcut" size="mini" @click="shiftReportYear(-1)">前一年</el-button>
          <el-button class="date-shortcut" size="mini" @click="shiftReportYear(1)">后一年</el-button>
        </el-form-item>
        <el-form-item label="站点名称">
          <el-select v-model="query.siteName" clearable filterable placeholder="请选择站点" style="width:200px">
            <el-option v-for="s in siteOptions" :key="s" :label="s" :value="s" />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">重置</el-button>
          <el-button type="primary" @click="search">查询</el-button>
        </el-form-item>
      </el-form>
    </div>

    <div class="table-panel">
      <div class="table-head">
        <span class="table-title">设备年汇总 <em>共 {{ total }} 条记录</em></span>
      </div>
      <el-table
        :data="reportListRows"
        :key="useTypedMetricColumns ? ('typed-' + (query.deviceType || '') + '-' + (query.protocolId || '')) : 'flow'"
        v-loading="loading"
        border
        size="small"
        class="report-table"
        :row-key="reportListRowKey"
        :row-class-name="reportRowClassName"
        :span-method="reportSpanMethod"
      >
        <el-table-column label="序号" width="60" align="center">
          <template slot-scope="{ row, $index }">{{ isReportSummaryRow(row) ? '' : rowIndex($index, row) }}</template>
        </el-table-column>
        <el-table-column column-key="periodLabel" prop="periodLabel" label="年份" width="80" align="center" />
        <el-table-column column-key="siteName" label="站点名称" min-width="140" show-overflow-tooltip>
          <template slot-scope="{ row }">{{ reportSiteName(row) || '—' }}</template>
        </el-table-column>
        <el-table-column column-key="deviceName" label="设备名称" min-width="140">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ reportDeviceName(row) }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ reportDeviceName(row) }}</a>
          </template>
        </el-table-column>
        <el-table-column v-if="showReportSubDevice" column-key="subDevice" label="子设备" min-width="100" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">{{ row._subLabel || '—' }}</span>
            <a v-else class="site-link" href="javascript:;" @click.prevent="openDetail(row)">{{ row._subLabel || '—' }}</a>
          </template>
        </el-table-column>
        <template v-if="useTypedMetricColumns">
          <el-table-column
            v-for="m in reportMetricColumns"
            :key="m.key"
            :label="formatMetricColumnLabel(m)"
            :min-width="m.width || 120"
            align="right"
          >
            <template slot-scope="{ row }">{{ formatNum(reportMetricValue(row, m)) }}</template>
          </el-table-column>
        </template>
        <template v-else>
          <el-table-column prop="monthCount" label="有数据月份" width="110" align="center" />
          <el-table-column prop="dayCount" label="上报天数" width="90" align="center" />
          <el-table-column label="年标况累积(m³)" min-width="130" align="right">
            <template slot-scope="{ row }">{{ formatNum(row.stdTotalSum) }}</template>
          </el-table-column>
          <el-table-column label="年均瞬时(m³/h)" min-width="120" align="right">
            <template slot-scope="{ row }">{{ formatNum(row.stdInstantAvg) }}</template>
          </el-table-column>
          <el-table-column prop="abnormalDays" label="异常天数" width="90" align="center" />
        </template>
        <el-table-column label="状态" width="96" align="center">
          <template slot-scope="{ row }">
            <span v-if="isReportSummaryRow(row)">—</span>
            <gas-status-tag v-else kind="reportRecord" :value="row.recordStatus" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right" align="center">
          <template slot-scope="{ row }">
            <el-button v-if="!isReportSummaryRow(row)" type="text" size="small" icon="el-icon-view" @click="openDetail(row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="canLoadCustomReport"
        class="pager" background layout="total, sizes, prev, pager, next, jumper"
        :total="total" :current-page="query.pageNum" :page-size="query.pageSize"
        :page-sizes="[10, 20, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <report-period-detail-drawer
      :visible.sync="detailVisible"
      mode="yearly"
      :title="detailTitle"
      :detail="detail"
      :rows="detailList"
      :metrics="useTypedMetricColumns ? reportMetricColumns : []"
      :show-sub-device="showReportSubDevice"
      :total="detailTotal"
      :query="detailQuery"
      :show-pager="false"
      :loading="detailLoading"
      :rows-loading="detailRowsLoading"
      table-title="每月统计数据"
      export-label="导出本年每月数据"
      @export="exportDetail"
      @closed="onDetailClosed"
    />
  </gas-page-shell>
</template>

<script>
import GasPageShell from '@/components/GasPageShell.vue'
import ReportPeriodDetailDrawer from '@/components/ReportPeriodDetailDrawer.vue'
import reportFlowCommon, { formatNum, currentYearText } from '@/mixins/reportFlowCommon'
import reportTableMerge from '@/mixins/reportTableMerge'
import deviceTypeOptionsMixin from '@/mixins/deviceTypeOptions'
import siteOptionsMixin from '@/mixins/siteOptions'
import reportProtocolFilter from '@/mixins/reportProtocolFilter'
import { gasApi } from '@/api/gas'

export default {
  components: { GasPageShell, ReportPeriodDetailDrawer },
  mixins: [reportFlowCommon, deviceTypeOptionsMixin, siteOptionsMixin, reportTableMerge, reportProtocolFilter],
  data() {
    return {
      detailVisible: false,
      detailLoading: false,
      detailRowsLoading: false,
      detail: null,
      detailList: [],
      detailTotal: 0,
      detailQuery: {
        pageNum: 1,
        pageSize: 12
      },
      projectCategoryOptions: [],
      query: {
        pageNum: 1,
        pageSize: 10,
        reportYear: currentYearText(),
        projectCategoryName: '',
        deviceType: '',
        protocolId: null,
        protocolName: '',
        siteName: '',
        deviceName: ''
      }
    }
  },
  created() {
    this.loadDeviceTypeOptions()
    this.loadSiteOptions()
    this.loadProjectCategoryOptions()
    this.loadStats()
    this.loadData()
  },
  computed: {
    canLoadCustomReport() {
      return true
    },
    useTypedMetricColumns() {
      return false
    },
    detailTitle() {
      if (!this.detail) return '年报详情'
      const name = this.reportDeviceName(this.detail) || '未命名设备'
      const sub = this.showReportSubDevice ? (this.detail.subDeviceLabel || this.detail._subLabel) : ''
      return sub ? `年报详情 — ${name} / ${sub}` : `年报详情 — ${name}`
    }
  },
  methods: {
    formatNum,
    projectCategoryLabel(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    projectCategoryValue(row) {
      return typeof row === 'string' ? row : (row && row.categoryName) || ''
    },
    async loadProjectCategoryOptions() {
      try {
        const res = await gasApi.projectCategory.options()
        const rows = res.data || []
        this.projectCategoryOptions = rows.map(this.projectCategoryValue).filter(Boolean)
      } catch (e) {
        this.projectCategoryOptions = []
      }
    },
    async loadStats() {
      const res = await gasApi.reportFlowYearly.stats(this.buildQuery())
      const d = res.data || {}
      this.statCards = [
        { label: '统计年份', value: d.reportYear || '-', icon: 'el-icon-date', color: '#1677ff', bg: '#e8f3ff' },
        { label: '设备总数', value: d.totalSites || 0, icon: 'el-icon-s-home', color: '#722ed1', bg: '#f9f0ff' },
        { label: '覆盖月份', value: d.reportMonths || 0, icon: 'el-icon-tickets', color: '#00b42a', bg: '#e8ffea' },
        { label: '年累计用气', value: formatNum(d.yearGasUsage), unit: 'm³', icon: 'el-icon-s-opportunity', color: '#ff7d00', bg: '#fff7e8' },
        { label: '异常设备', value: d.abnormalSites || 0, icon: 'el-icon-warning-outline', color: '#f53f3f', bg: '#ffece8' }
      ]
    },
    async loadData() {
      if (!this.canLoadCustomReport) {
        this.list = []
        this.total = 0
        return
      }
      this.loading = true
      try {
        const res = await gasApi.reportFlowYearly.list(this.buildQuery())
        const data = res.data || {}
        this.list = data.records || []
        this.total = Number(data.total || 0)
      } finally {
        this.loading = false
      }
    },
    resetSearch() {
      this.query = {
        pageNum: 1,
        pageSize: 10,
        reportYear: currentYearText(),
        projectCategoryName: '',
        deviceType: '',
        protocolId: null,
        protocolName: '',
        siteName: '',
        deviceName: ''
      }
      this.customProtocolMetrics = []
      this.loadStats()
      this.loadData()
    },
    shiftReportYear(offset) {
      const base = this.query.reportYear ? new Date(`${this.query.reportYear}-01-01T00:00:00`) : new Date()
      base.setFullYear(base.getFullYear() + offset)
      this.query.reportYear = String(base.getFullYear())
      this.search()
    },
    async openDetail(row) {
      if (this.isReportSummaryRow(row)) return
      this.detail = { ...row }
      this.detailVisible = true
      this.detailQuery.pageNum = 1
      this.detailQuery.pageSize = 12
      await this.loadDetail(true)
    },
    detailParams(extra = {}) {
      const d = this.detail || {}
      return {
        reportYear: d.periodLabel || this.query.reportYear,
        regionName: d.regionName,
        siteType: d.siteType,
        siteName: this.reportDeviceName(d) || d.siteName || this.query.siteName,
        reportType: d.reportType,
        deviceId: d.deviceId,
        terminalCode: d.terminalCode,
        deviceCode: d.deviceCode,
        identityKey: d.identityKey,
        subDeviceCode: d.subDeviceCode || d._subCode || undefined,
        pageNum: this.detailQuery.pageNum,
        pageSize: this.detailQuery.pageSize,
        ...extra
      }
    },
    async loadDetail(loadSummary) {
      if (!this.detail) return
      const clicked = {
        subDeviceCode: this.detail.subDeviceCode || this.detail._subCode || '',
        subDeviceLabel: this.detail.subDeviceLabel || this.detail._subLabel || ''
      }
      if (loadSummary) this.detailLoading = true
      this.detailRowsLoading = true
      try {
        const res = await gasApi.reportFlowYearly.detail(this.detailParams())
        const data = res.data || {}
        this.detail = Object.assign({}, this.detail, data.detail || {}, {
          subDeviceCode: (data.detail && data.detail.subDeviceCode) || clicked.subDeviceCode,
          subDeviceLabel: (data.detail && data.detail.subDeviceLabel) || clicked.subDeviceLabel
        })
        this.detailList = data.records || []
        this.detailTotal = Number(data.total || 0)
      } finally {
        this.detailLoading = false
        this.detailRowsLoading = false
      }
    },
    onDetailClosed() {
      this.detail = null
      this.detailList = []
      this.detailTotal = 0
      this.detailQuery.pageNum = 1
      this.detailQuery.pageSize = 12
    },
    async exportDetail() {
      if (!this.detail) return this.$message.warning('暂无可导出的详情数据')
      const sub = this.detail.subDeviceLabel || this.detail._subLabel || ''
      const filename = `年报每月数据_${this.reportDeviceName(this.detail) || ''}_${sub}_${this.detail.periodLabel || this.query.reportYear}`
      let rows = this.detailList
      if (!rows.length) {
        const res = await gasApi.reportFlowYearly.detail(this.detailParams({ pageNum: 1, pageSize: 12 }))
        const data = res.data || {}
        rows = data.records || []
      }
      if (this.useTypedMetricColumns && this.reportMetricColumns.length) {
        const metrics = this.reportMetricColumns
        this.downloadCsv(rows,
          ['统计月份', '设备名称', '子设备', ...metrics.map(m => this.formatMetricColumnLabel(m)), '状态'],
          r => [r.monthLabel, this.reportDeviceName(r), r.subDeviceLabel || sub,
            ...metrics.map(m => this.reportMetricValue(r, m)), this.statusLabel(r.recordStatus)],
          filename)
        return
      }
      this.downloadPeriodDetailCsv(rows, 'yearly', filename)
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/styles/report-flow.scss';
</style>
<style>
.report-table tr.report-summary-row > td {
  background: #f7f8fa !important;
  font-weight: 600;
  color: #1d2129;
}
.report-table tr.report-summary-row.is-site > td {
  background: #eef4ff !important;
}
</style>
